import { type FC, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import './_fansList.scss'

import {
  IconEdit,
  IconThreeDotsVertical,
  IconTrashcan,
  IconTwoPeople
} from 'assets/svg/sprite'
import { OptionsDropdown } from 'components-ref/molecules'
import { type IOption } from 'types/iTypes'

const FansList: FC<{
  title: string
  fans: { avatars: string[]; count: number; id?: string; data?: any }
  clickFn?: () => void
  customClass?: string
  type?: 'fans' | 'subscriptions' | 'creators'
  onDelete?: (id: number) => void
  onEdit?: (id: string, name: string) => void
  id?: number | any
  name?: string | any
  showZeroCount?: boolean
  hideOptions?: boolean
}> = ({ title, fans, clickFn, customClass, type = 'fans', onDelete, onEdit, name, showZeroCount = false, hideOptions = false}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isOptionsNavOpen, setIsOptionsNavOpen] = useState(false)

  const listId = fans?.data?.id
  const listName = (fans?.data?.name ?? name ?? title) as string
  const isDefaultGroup = Number(fans?.data?.default) === 1

    const fansNumber = () => {
    if (!fans) return t('empty')

    const count = Number.isFinite(Number(fans.count)) ? Number(fans.count) : 0

    // only treat 0 as "empty" if we did NOT ask to show zeros
    if (count === 0 && !showZeroCount) return t('empty')
    const label =
        type === 'fans'
        ? 'Fans'
        : type === 'subscriptions'
        ? 'Subscriptions'
        : 'Creators'

    if (fans.count === 1) {
        const singleLabel =
        type === 'fans'
            ? 'Fan'
            : type === 'subscriptions'
            ? 'Subscription'
            : 'Creator'
        return `${fans.count} ${singleLabel}`
    }

    if (fans.count < 1000) {
        return `${fans.count} ${label}`
    }

    return `+${parseFloat((fans.count / 1000).toFixed(1))}k ${label}`
    }

  // Custom options (rename, delete) – call the handlers you passed in
  const optionsArray: IOption[] = useMemo(() => {
    return [
      {
        text: t('renameList'),
        icon: <IconEdit />,
        disabled: false,
        action: (idFromDropdown: string) => {
          onEdit?.(idFromDropdown, listName)
          setIsOptionsNavOpen(false)
        }
      }
    ]
  }, [t, onEdit, listName])

  return (
    <div
      className={`fansList ${customClass ?? ''}`}
      onClick={() => {
        clickFn?.()
      }}
    >
      <div
        className="fansList__left"
        onClick={() => {
          if (listId != null) {
            navigate(`/subscriptions/${listId}`, { state: { listName } })
          }
        }}
      >
    <div className="fansList__left-icon">
     <IconTwoPeople/>
    </div>
    <div className="fansList__left-text">
        <h2 className="fansList__left__title">{title}</h2>
        <p className="fansList__left__fansNumber">{fansNumber()}</p>
    </div>
    </div>

      {!hideOptions && !isDefaultGroup && (
        <div className="fansList__right">
          <button
            type="button"
            className="fansList__optionsBtn"
            aria-label={t('options')}
            onClick={(e) => {
              e.stopPropagation()
              setIsOptionsNavOpen(true)
            }}
          >
            <IconThreeDotsVertical color="grey" />
          </button>
      {isOptionsNavOpen && (
        <OptionsDropdown
          role="owner"
          content="list"
          isOptionsNavOpen={isOptionsNavOpen}
          setIsOptionsNavOpen={setIsOptionsNavOpen}
          postId={listId}
          options={optionsArray}
          deleteFn={() => {
            if (listId != null) onDelete?.(listId)
          }}
        />
        )}
        </div>
      )}
    </div>
  )
}

export default FansList
